import { CheckCircle2, ClipboardList } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { PreviewTable } from '../../../../components/PreviewTable';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { COMPETENCY_LEVEL_LABELS } from '../../../human-resources/personnel/personnelTypes';
import { SurveyTrainingRecommendationsPanel } from '../components/SurveyTrainingRecommendationsPanel';
import { developmentPlansApi } from '../../development-plan/developmentPlanApi';
import { surveyPlansApi } from '../surveyApi';
import type { SurveyPlanDetail } from '../surveyTypes';

type TabKey = 'info' | 'training';

/**
 * A development plan's profile — what Survey › Development Plan's Title opens (product call,
 * 12 Oct 2026): the plan AS CREATED, and the employees it was created for.
 *
 * On the **header** layout: a plan reads as one continuous thing — its details, its needs, its
 * document — with its Training Recommendations a second tab (the employees are on each
 * recommendation's own profile). It is READ ONLY: each employee holds their own copy,
 * revised, approved and trained through their own plan, which their row links to. So there is no
 * Edit here, and the cards say nothing that a copy could have since changed.
 */
export function SurveyPlanProfilePage({ from = 'survey' }: { from?: 'survey' | 'development-plans' }) {
  const { surveyId, planId } = useParams<{ surveyId: string; planId: string }>();
  /*
    ONE profile, two homes (product call, 12 Oct 2026): opened from a survey's own Development Plan
    tab, or from Learning & Development › Development Plan, whose register spans every survey. The
    second names no survey in its route and reads on that module's own key — and its breadcrumb
    leads back to where the reader came from. Everything drawn is the same.
  */
  const fromRegister = from === 'development-plans';
  const navigate = useNavigate();

  const [detail, setDetail] = useState<SurveyPlanDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  // LANDS ON TRAINING RECOMMENDATIONS (product call, 13 Oct 2026) — what the plan is opened to work on.
  // `Info` is still the first tab DECLARED; only where the page lands differs.
  const [tab, setTab] = useState<TabKey>('training');

  useEffect(() => {
    if (!planId || (!fromRegister && !surveyId)) return;

    const controller = new AbortController();

    (fromRegister
      ? developmentPlansApi.getPlan(planId, controller.signal)
      : surveyPlansApi.get(surveyId!, planId, controller.signal)
    )
      .then((next) => {
        if (!controller.signal.aborted) setDetail(next);
      })
      .catch(async (caught: unknown) => {
        if (controller.signal.aborted) return;

        const failure = caught instanceof ApiError ? caught : new ApiError('Could not load this development plan.', 0);

        /*
          A RETIRED ADDRESS REDIRECTS, and carries its id across. Until 12 Oct 2026 this register's
          `/development-plan/:id` was an EMPLOYEE's own plan, the id their response's. A bookmark or
          an open tab from before still names one — so a 404 here asks whether the id is a
          response's, and if it is, lands the reader on that employee's profile under its plan
          rather than on "not found". Only then; any other failure is reported as it is.
        */
        if (fromRegister && failure.status === 404) {
          try {
            const employee = await developmentPlansApi.get(planId, controller.signal);

            if (controller.signal.aborted) return;

            if (employee.summary.planId != null) {
              navigate(`/learning-development/development-plan/${employee.summary.planId}/personnel/${planId}`, {
                replace: true,
              });

              return;
            }
          } catch {
            // Not a response's id either — the original "not found" stands.
          }

          if (controller.signal.aborted) return;
        }

        setError(failure);
      });

    return () => controller.abort();
  }, [surveyId, planId, fromRegister, navigate]);

  const breadcrumb: BreadcrumbSegment[] = fromRegister
    ? [
        'Learning & Development',
        { label: 'Development Plan', to: '/learning-development/development-plan' },
        detail ? detail.plan.title : 'Development plan',
      ]
    : [
        'Learning & Development',
        { label: 'Survey', to: '/learning-development/survey' },
        { label: detail?.surveyTitle ?? 'Survey', to: `/learning-development/survey/${surveyId}` },
        detail ? detail.plan.title : 'Development plan',
      ];

  if (error && !detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { plan } = detail;

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="survey-plan-profile"
        header={
          <ProfileHeader
            id="survey-plan-profile-header"
            icon={<ClipboardList className="h-6 w-6" aria-hidden="true" />}
            title={plan.title}
            meta={[
              `${plan.members.length} ${plan.members.length === 1 ? 'employee' : 'employees'}`,
              `${plan.needCount} ${plan.needCount === 1 ? 'development need' : 'development needs'}`,
            ]}
            secondary={[detail.surveyTitle]}
            status={<StatusText tone={plan.isActive ? 'active' : 'inactive'}>{plan.isActive ? 'Active' : 'Deleted'}</StatusText>}
            createdBy={plan.createdByName}
            createdAt={plan.createdAt}
          />
        }
        banner={
          <ProfileBanner
            id="survey-plan-profile-banner"
            icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
            message="This is the plan as it was created. Each employee holds their own copy, which is revised, approved and sent from their own plan."
          />
        }
        // The table owns its own chrome, so its tab runs edge to edge; Info keeps the card column.
        panel={tab === 'info' ? 'cards' : 'bleed'}
        tabs={[
          { key: 'info', label: 'Info' },
          // Its own tab (product call, 12 Oct 2026) — the create form's second step, as its own section here.
          { key: 'training', label: 'Training Recommendations' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            {/* No Edit on any of these: they are a record of what was created, as Audit is. */}
            <ProfileCard id="survey-plan-profile-details" title="Details">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Title', value: plan.title },
                  { label: 'Survey', value: detail.surveyTitle },
                  { label: 'Created by', value: plan.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(plan.createdAt)} className="font-mono">
                        {formatDateTime(plan.createdAt)}
                      </time>
                    ),
                  },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="survey-plan-profile-needs" title="Identified Development Needs" count={detail.needs.length}>
              <PreviewGrid
                columns={[
                  { header: 'Competency', width: 220, render: (n) => <TextCell value={n.competencyName} /> },
                  { header: 'Current level', width: 140, render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.currentLevel]} /> },
                  { header: 'Target level', width: 140, render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.targetLevel]} /> },
                  { header: 'Gap identified', width: 260, render: (n) => <TextCell value={n.gap ?? null} /> },
                ]}
                rows={detail.needs}
                rowKey={(n) => n.id}
                empty="No development needs were identified on this plan."
              />
            </ProfileCard>

            <ProfileCard id="survey-plan-profile-document" title="Development Plan" padded>
              {/* The one renderer of stored markup — safe only because the server sanitised it on write. */}
              <RichTextDocument html={detail.document ?? null} />
            </ProfileCard>
          </>
        ) : (
          /*
            The plan's Training Recommendations — the standard table, with Create (product call,
            12 Oct 2026). Each title opens the recommendation's own profile, where its approval is
            tracked and where the plan's Personnel now sits.
          */
          <SurveyTrainingRecommendationsPanel surveyId={plan.surveyId} planId={plan.id} planActive={plan.isActive} />
        )}
      </ProfileHeaderShell>
    </div>
  );
}
